import React, {useEffect, useMemo, useState} from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControl, FormControlLabel, FormGroup,
  Grid, InputAdornment, InputLabel,
  MenuItem, Popper,
  Select, TextField as MuiTextField,
} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../shared/state/root-reducer';
import '../../../../../scss/components/_forms.scss';
import {TextField} from '@finpay-development/shared-components';
import {FormikProps} from 'formik';
import {
  emptyPatientCurrentInsurance,
} from '../../../../../patient/components/models/patient-view-model';
import SearchIcon from '@mui/icons-material/Search';
import { clearSelectedEstimate } from '../../../../../shared/state/estimator-slice';
import { AppDispatch } from '../../../../../shared/state/store';
import { Payor, Plan } from '@finpay-estimation/estimation-types';
import { estimateService } from '../../../../services/estimate-service';
import { showErrorStatus } from '../../../../../security/state/user-slice';

function payorDisplayName(p: Payor): string {
  return (p.facilityPayorName ?? '').trim();
}

function payorPlansList(p: Payor | undefined): Plan[] {
  return p?.plans ?? [];
}

function findPayorByPayorId(
    list: Payor[],
    payorId: number | undefined | null,
): Payor | undefined {
  if (payorId == null || payorId <= 0) {
    return undefined;
  }
  return list.find((p) => p.payorId === payorId);
}

export interface InsuranceInfoSectionProps {
  formik: FormikProps<any>;
}

export const InsuranceInfoSection: React.FC<InsuranceInfoSectionProps> = ({formik}) => {

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    initialValues,
    setValues,
    touched,
    errors,
  } = formik;

  /** As returned by `estimation/v2/client/{clientId}/current-insurance/payor`. */
  const [currentInsurancePayors, setCurrentInsurancePayors] = useState<Payor[]>([]);
  const [isPayorsLoading, setIsPayorsLoading] = useState(false);

  const subscriberRelation = useSelector((state: RootState) => {
    return state.metaData.subscriberRelation;
  });

  const selfPayPayor = useMemo(() => {
    return currentInsurancePayors.find((payor) => {
      return payorDisplayName(payor).toLowerCase() === 'self-pay';
    });
  }, [currentInsurancePayors]);

  const selectedPayorRow = useMemo(() => {
    return findPayorByPayorId(currentInsurancePayors, values.payorId);
  }, [currentInsurancePayors, values.payorId]);

  const isDisabled = useMemo(() => {
    return values.payorId === selfPayPayor?.payorId;
  }, [values.payorId, selfPayPayor?.payorId]);

  const selectedEstimate = useSelector(
      (state: RootState) => state.estimatorContext.selectedEstimate
  );
  const dispatch = useDispatch<AppDispatch>();

  const clientId = values.clientId;

  useEffect(() => {
    if (!clientId || clientId <= 0) {
      setCurrentInsurancePayors([]);
      setIsPayorsLoading(false);
      return;
    }

    const ac = new AbortController();
    setCurrentInsurancePayors([]);
    setIsPayorsLoading(true);

    (async () => {
      try {
        const res = await estimateService.getCurrentInsurancePayors(clientId, ac.signal);
        if (ac.signal.aborted) {
          return;
        }
        if (res.hasErrors) {
          dispatch(showErrorStatus(res.errorMessage || 'Failed to load payors for this client'));
          setCurrentInsurancePayors([]);
          return;
        }
        setCurrentInsurancePayors(res.entity);
      } catch (e: unknown) {
        if (ac.signal.aborted) {
          return;
        }
        dispatch(showErrorStatus(e instanceof Error ? e.message : 'Failed to load payors'));
        setCurrentInsurancePayors([]);
      } finally {
        if (!ac.signal.aborted) {
          setIsPayorsLoading(false);
        }
      }
    })();

    return () => {
      ac.abort();
      setIsPayorsLoading(false);
    };
  }, [clientId, dispatch]);

  useEffect(() => {
    if (isPayorsLoading) {
      return;
    }
    if (currentInsurancePayors.length === 0) {
      return;
    }
    const pid = values.payorId;
    if (!pid || pid <= 0) {
      return;
    }
    if (findPayorByPayorId(currentInsurancePayors, pid)) {
      return;
    }
    setValues({
      ...values,
      payorId: emptyPatientCurrentInsurance.payorId,
      payorPlanId: emptyPatientCurrentInsurance.payorPlanId,
      subscriberName: emptyPatientCurrentInsurance.subscriberName,
      subscriberRelation: emptyPatientCurrentInsurance.subscriberRelation,
      memberId: emptyPatientCurrentInsurance.memberId,
      groupNumber: emptyPatientCurrentInsurance.groupNumber,
    });
    if (selectedEstimate) {
      dispatch(clearSelectedEstimate());
    }
  }, [
    isPayorsLoading,
    currentInsurancePayors,
    values,
    setValues,
    dispatch,
    selectedEstimate,
  ]);

  const payerInputLoading = isPayorsLoading && Boolean(clientId && clientId > 0);

  return (
      <Grid container spacing={2}>
        <Grid
            item
            direction="column"
            xs={12}
            style={{
              padding: '0 1em',
            }}
        >
          <FormGroup>
            <FormControlLabel
                sx={{ width: 'fit-content', flex: '0 0 auto' }}
                control={
                  <Checkbox
                      checked={
                        payorDisplayName(
                            findPayorByPayorId(currentInsurancePayors, values.payorId) ?? {},
                        ).toLowerCase() === 'self-pay'
                      }
                      onClick={() => {
                        if (values.payorId === selfPayPayor?.payorId) {

                          if (initialValues.payorId === selfPayPayor?.payorId) {
                            setValues({
                              ...values,
                              payorId: emptyPatientCurrentInsurance.payorId,
                              payorPlanId: emptyPatientCurrentInsurance.payorPlanId,
                              subscriberName: emptyPatientCurrentInsurance.subscriberName,
                              subscriberRelation: emptyPatientCurrentInsurance.subscriberRelation,
                              memberId: emptyPatientCurrentInsurance.memberId,
                              groupNumber: emptyPatientCurrentInsurance.groupNumber,
                            });

                          } else {
                            setValues({
                              ...values,
                              payorId: initialValues.payorId,
                              payorPlanId: initialValues.payorPlanId,
                              subscriberName: initialValues.subscriberName,
                              subscriberRelation: initialValues.subscriberRelation,
                              memberId: initialValues.memberId,
                              groupNumber: initialValues.groupNumber,
                            });
                          }

                        } else {
                          setValues({
                            ...values,
                            payorId: selfPayPayor?.payorId,
                            payorPlanId: payorPlansList(selfPayPayor)[0]?.payorPlanId,
                            subscriberName: emptyPatientCurrentInsurance.subscriberName,
                            subscriberRelation: emptyPatientCurrentInsurance.subscriberRelation,
                            memberId: emptyPatientCurrentInsurance.memberId,
                            groupNumber: emptyPatientCurrentInsurance.groupNumber,
                          });
                        }
                        // clear out estimate if exists
                        if (selectedEstimate) {
                          dispatch(clearSelectedEstimate());
                        }
                      }
                      }
                      test-id="patient-tab-private-pay"
                  />
                }
                label="Uninsured"
            />
          </FormGroup>
        </Grid>
        <Grid item xs={6} >
          <Autocomplete
              getOptionLabel={(option) => {
                return payorDisplayName(option);
              }}
              isOptionEqualToValue={(a, b) => (a?.payorId ?? 0) === (b?.payorId ?? 0)}
              value={selectedPayorRow || null}
              disabled={isDisabled || payerInputLoading}
              loading={payerInputLoading}
              onChange={(e, value) => {
                const selectedPayorId = value?.payorId;
                if (value?.payorId && selectedPayorId ===
                    selfPayPayor?.payorId) {
                  setValues({
                    ...values,
                    payorId: selectedPayorId,
                    payorPlanId: payorPlansList(selfPayPayor)[0]?.payorPlanId,
                    subscriberName: '',
                    subscriberRelation: -1,
                    memberId: '',
                    groupNumber: '',
                  });
                } else {
                  setValues({
                    ...values,
                    payorId: selectedPayorId,
                    payorPlanId: 0,
                  });
                }
              }}
              renderInput={(params) => {
                return (
                    <MuiTextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon/>
                              </InputAdornment>
                          ),
                        }}
                        label="Payer"
                        placeholder="Search Payers"
                    />
                );
              }}
              renderOption={(props, option) => {
                const label = payorDisplayName(option);
                const hasFacilityPayorId = option.facilityPayorId != null;
                return (
                  <li {...props}>
                    {hasFacilityPayorId ? (
                        <strong>{label}</strong>
                    ) : (
                        <span>{label}</span>
                    )}
                  </li>
                );
              }}
              PopperComponent={(props) => {
                return (
                    <Popper {...props} placement="bottom-start"
                            modifiers={[{name: 'flip', enabled: false}]}/>
                );
              }
              }
              options={currentInsurancePayors}
              test-id="patient-tab-payor"
              className={`${touched.payorId &&
              Boolean(errors.payorId) ?
                  'error-border' :
                  'required-fields'}`}
          />
        </Grid>
        <Grid item xs={6} className="MuiTextField-root">
          <FormControl fullWidth size="small">
            <InputLabel id="plan-select">Plan</InputLabel>
            <Select
                labelId="plan-select"
                label="Plan"
                value={values.payorPlanId}
                onChange={(e) => {
                  let planValue = (e.target as HTMLInputElement).value;
                  if (planValue) {
                    setFieldValue('payorPlanId', planValue);
                  }
                }}
                style={{
                  width: '100%',
                }}
                renderValue={(selected) => {
                  const selectedPlan = payorPlansList(selectedPayorRow).find((plan) => {
                    return plan.payorPlanId === selected;
                  });
                  return selectedPlan?.planName || 'Select Plan';

                }}
                disabled={isDisabled || payerInputLoading}
                className={`state-field ${touched.payorPlanId &&
                Boolean(errors.payorPlanId) ?
                    'error-border' :
                    'required-fields'}`}
                required
                test-id="patient-tab-plan"
            >
              {
                payorPlansList(selectedPayorRow).
                    map((plan) => {
                      return (
                          <MenuItem
                              key={plan.payorPlanId}
                              value={plan.payorPlanId}
                          >
                            {plan.planName}
                          </MenuItem>
                      );
                    })
              }
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
              name="memberId"
              placeholder="Enter Member ID"
              value={values.memberId}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Member ID"
              disabled={isDisabled}
              className={`${touched.memberId && Boolean(errors.memberId) ?
                  'error-border' :
                  ''}`}
              test-id="patient-tab-member-id"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
              name="groupNumber"
              placeholder="Enter Group Number"
              value={values.groupNumber}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Group Number"
              disabled={isDisabled}
              className={`${touched.groupNumber && Boolean(errors.groupNumber) ?
                  'error-border' :
                  ''}`}
              test-id="patient-tab-group-number"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
              name="subscriberName"
              placeholder="Enter Subscriber"
              value={values.subscriberName}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Subscriber"
              disabled={isDisabled}
              className={`${touched.subscriberName &&
              Boolean(errors.subscriberName) ? 'error-border' : ''}`}
              test-id="patient-tab-subscriber-name"
          />
        </Grid>
        <Grid item xs={6} className="MuiTextField-root">
          <FormControl fullWidth size="small">
            <InputLabel id="subscriber-relation">Relationship</InputLabel>
            <Select
                labelId="subscriber-relation"
                label="Relationship"
                value={values.subscriberRelation}
                onChange={(e) => {
                  let relation = (e.target as HTMLInputElement).value;
                  setFieldValue('subscriberRelation', relation);
                }}
                style={{
                  width: '100%',
                }}
                renderValue={(selected) => {
                  const selectedSubscriberRelation = subscriberRelation.find(
                      (relation) => {
                        return relation.subscriberRelationId === selected;
                      });
                  return selectedSubscriberRelation?.subscriberRelation ||
                      'Select Relationship';
                }}
                disabled={isDisabled || subscriberRelation.length === 0}
                className={`${touched.subscriberRelation &&
                Boolean(errors.subscriberRelation) ? 'error-border' : ''}`}
                test-id="patient-tab-subscriber-relation"
            >
              {subscriberRelation.map(
                  ({subscriberRelation, subscriberRelationId}: {
                    subscriberRelationId: number,
                    subscriberRelation: string
                  }) => (
                      <MenuItem key={subscriberRelationId}
                                value={subscriberRelationId}>
                        {subscriberRelation}
                      </MenuItem>
                  ),
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
  );
};
